<script lang="ts">
  import PriceWrapper from "@retailer/svelte/Shared/PriceWrapper.svelte";
  import { getInventory } from "@retailer/svelte/ProductDetails/Api.svelte";
  import { EventTracking } from "@shared/services/event_tracking/events";
  import { OrderHistoryReorderEntity } from "@shared/services/event_tracking/entities";
  import OrderHistoryCartWidgetContainer from "@retailer/svelte/CartWidget/v2/Containers/OrderHistoryCartWidgetContainer.svelte";
  import UIRouterLink from "@shared/svelte/Components/UIRouterLink.svelte";
  import { getControlStateAuthenticationService } from "@retailer/services/retailer_user_authentications_service";
  import { stagedRolloutCheck } from "@shared/svelte/Modules/StagedRolloutCheck.svelte";
  import Icon from "@shared/svelte/Components/Icon.svelte";
  import RetailerUserAuthenticationsStore from "@retailer/svelte/stores/RetailerUserAuthenticationsStore";

  import type {
    CartProduct,
    CartProductProductLine,
    Inventory,
    ProductLine,
  } from "@shared/types/ProductLine";
  import type { Distributor } from "@shared/types/Distributor";
  import { InventoryItemStore } from "@retailer/svelte/CartWidget/v2/Stores/InventoryItemStore";

  export let product: CartProduct;
  export let productLine: CartProductProductLine | ProductLine;
  export let distributor: Distributor;

  let inventory: Inventory;
  let isOpen = false;
  let hasOpened = false;

  $: displayInventoryError = $InventoryItemStore.inventoryItems[
    product.inventory_id
  ]?.notices?.some(notice => notice.level === "error");

  const { trackClick, trackEvent } = EventTracking;
  const trackingEntities = [
    new OrderHistoryReorderEntity({
      distributorId: distributor.id,
      orderId: product.order_id,
      inventoryId: product.inventory_id,
      productId: product.id,
      productLineId: productLine.id,
    }),
  ];

  const onError = () => {
    displayInventoryError = true;
    trackEvent("inventory_load_failed", trackingEntities);
  };

  const onLoad = (response: Inventory) => {
    inventory = response;
  };

  $: if (isOpen) hasOpened = true;

  $: {
    if (hasOpened) {
      trackClick("expand_accordion", trackingEntities);
      getInventory(product.inventory_id, onLoad, onError);
    }
  }

  const hasRetailerUserAuthRollout = stagedRolloutCheck(
    "marketplace__retailer_user_authentication",
  );

  // auth service
  let authService = getControlStateAuthenticationService();

  let hideAllDistributorInfo: boolean = false;
  $: hideAllDistributorInfo = authService?.shouldHideAllDistributorInfo?.(
    $RetailerUserAuthenticationsStore,
    [distributor.id],
  );

  let authStatus;
  $: authStatus = authService?.getRetailerUserAuthenticationStatus?.(
    $RetailerUserAuthenticationsStore,
    [distributor.id],
  );

  $: orderFulfillmentWarehouseLocationCode =
    stagedRolloutCheck("marketplace__fulfillment_methods") && distributor?.uses_wine_cellar
      ? product.warehouse_location_code || distributor?.default_stock_site_id
      : null;
</script>

<div class="fizz-card fizz-bordered" id={`reorder-${product.inventory_id}`}>
  <details class="fizz-accordion fizz-accordion--small" bind:open={isOpen}>
    <summary class="fizz-text-strong">Order it again</summary>
    <div class="fizz-inset-square-16" class:fizz-flow-8={!displayInventoryError}>
      {#if hasOpened}
        {#if hideAllDistributorInfo}
          <div class="fizz-flex-row-4">
            {#if authStatus == "pending"}
              <Icon type="InfoYellow" color="red" height={16} width={16} />
              <div class="fizz-text-subdued">
                {authService?.pendingDescription}
              </div>
            {:else if authStatus == "invalid"}
              <Icon type="Error" color="red" height={16} width={16} />
              <div class="fizz-text-negative">
                {authService?.invalidDescription}
              </div>
            {/if}
          </div>
        {:else}
          {#if !displayInventoryError}
            <div class="fizz-flex-row-4">
              <PriceWrapper
                inventoryId={product.inventory_id}
                productTypeId={productLine.product_type_id}
                warehouseLocationCode={orderFulfillmentWarehouseLocationCode}
                {trackingEntities}
              />
            </div>
          {/if}
          {#if inventory}
            <OrderHistoryCartWidgetContainer
              orderFulfillmentWarehouse={{ id: product.warehouse_id, name: product.warehouse_name }}
              {orderFulfillmentWarehouseLocationCode}
              inventoryId={product.inventory_id}
              {trackingEntities}
            />
          {/if}

          {#if displayInventoryError}
            <div class="fizz-text-negative">
              <p>
                Changes to inventory may have impacted this product's availability.
                <UIRouterLink
                  classes="fizz-text-inherit"
                  stateName="layout.product_detail"
                  params={{ productId: productLine.id }}
                >
                  View all sizes
                </UIRouterLink>
              </p>
            </div>
          {/if}
        {/if}
      {/if}
    </div>
  </details>
</div>
